import React from 'react';
import {
  Box,
  CardMedia,
  Chip,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import { format } from 'date-fns';
import { uk } from 'date-fns/locale';

import { ClientExpertPostDetail } from '@/types/expert-posts';
import { getImageUrl } from '@/utils/image-url.util';
import { BaseDialog } from '../common/BaseDialog';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';

interface ExpertPostDetailModalProps {
  post: ClientExpertPostDetail | null;
  loading: boolean;
  onClose: () => void;
}

export const ExpertPostDetailModal: React.FC<ExpertPostDetailModalProps> = ({
  post,
  loading,
  onClose,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  if (!post) return null;

  return (
    <BaseDialog
      open
      onClose={onClose}
      maxWidth="md"
      fullScreen={fullScreen}
      iDoc={ModalIndices.EXPERT_POST_DETAIL}
      headerMedia={
        post.coverImageUrl ? (
          <CardMedia
            component="img"
            height={fullScreen ? '250' : '400'}
            image={getImageUrl(post.coverImageUrl)}
            alt={post.title}
            sx={{ objectFit: 'cover' }}
          />
        ) : undefined
      }
      title={
        <Box>
          <Typography variant="h5" fontWeight="bold">
            {post.title}
          </Typography>
          {post.publishedAt && (
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <Chip
                icon={<CalendarToday sx={{ fontSize: '16px !important' }} />}
                label={format(new Date(post.publishedAt), 'd MMMM yyyy', { locale: uk })}
                size="small"
                variant="outlined"
              />
            </Box>
          )}
        </Box>
      }
    >
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
          {post.content}
        </Typography>
      )}
    </BaseDialog>
  );
};
